import React from 'react';
import { Card, Button, Table, Row, Col, Alert } from 'react-bootstrap';
import { MENU_CATEGORIES } from '../../utils/constants';

function WeeklyReportTab({ weeklyReport, onLoadReport, onPrintReport }) {
  const renderDaySlotSummary = (dayName, slotName, slotData) => {
    if (!slotData || slotData.customers.length === 0) return null;

    const totals = Object.fromEntries(MENU_CATEGORIES.map(c => [c, {}]));

    // Calculate totals
    slotData.customers.forEach(customer => {
      Object.entries(customer.items).forEach(([category, items]) => {
        items.forEach(item => {
          if (!totals[category][item.itemName]) {
            totals[category][item.itemName] = 0;
          }
          totals[category][item.itemName] += item.quantity;
        });
      });
    });

    return (
      <Card key={`${dayName}-${slotName}`} className="mb-3">
        <Card.Header className="bg-light">
          <strong>{slotName}</strong>
          <small className="text-muted ms-2">
            ({slotData.customers.length} customers)
            {slotData.cutoffTime && ` • Cutoff: ${slotData.cutoffTime}`}
          </small>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <h6 className="text-primary">Customer Details</h6>
              <Table size="sm" striped>
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Items</th>
                  </tr>
                </thead>
                <tbody>
                  {slotData.customers.map((customer, idx) => (
                    <tr key={idx}>
                      <td><strong>{customer.locationName}</strong></td>
                      <td>
                        {MENU_CATEGORIES.map(category => (
                          customer.items[category].length > 0 && (
                            <div key={category}>
                              <small className="text-muted">{category}:</small>
                              <ul style={{ marginBottom: '5px', paddingLeft: '20px' }}>
                                {customer.items[category].map((item, i) => (
                                  <li key={i} style={{ fontSize: '0.9rem' }}>
                                    {item.itemName} ({item.quantity})
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
            <Col md={6}>
              <h6 className="text-success">Totals to Prepare</h6>
              {MENU_CATEGORIES.map(category => {
                const items = Object.entries(totals[category]);
                if (items.length === 0) return null;
                
                return (
                  <div key={category} className="mb-3">
                    <strong>{category}</strong>
                    <Table size="sm" bordered className="mt-2">
                      <tbody>
                        {items.map(([itemName, qty]) => (
                          <tr key={itemName}>
                            <td>{itemName}</td>
                            <td className="text-end" style={{ width: '80px' }}>
                              <strong>{qty}</strong>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                );
              })}
            </Col>
          </Row>
        </Card.Body>
      </Card>
    );
  };

  return (
    <Card className="fade-in">
      <Card.Header>
        📊 Weekly Standing Orders Report
        <Button
          variant="primary"
          size="sm"
          className="float-end ms-2"
          onClick={onLoadReport}
        >
          🔄 Generate Report
        </Button>
        {weeklyReport && (
          <Button
            variant="success"
            size="sm"
            className="float-end"
            onClick={onPrintReport}
          >
            🖨️ Print Version
          </Button>
        )}
      </Card.Header>
      <Card.Body>
        {!weeklyReport ? (
          <div className="text-center text-muted py-5">
            <h5>Click "Generate Report" to create weekly summary</h5>
            <p>This report shows all standing orders organized by day and time slot</p>
          </div>
        ) : (
          <>
            <Alert variant="info">
              <strong>Report Generated:</strong> {new Date(weeklyReport.generatedAt).toLocaleString()}
              <br />
              <strong>Active Customers:</strong> {weeklyReport.metadata.totalCustomers}
              <br />
              <strong>Total Standing Orders:</strong> {weeklyReport.metadata.totalActiveStandingOrders}
            </Alert>

            {Object.entries(weeklyReport.reportByDay).map(([dayName, dayData]) => {
              // Check if day has any orders
              const hasOrders = Object.values(dayData.slots).some(
                slot => slot.customers.length > 0
              );

              if (!hasOrders) return null;

              return (
                <div key={dayName} className="mb-5">
                  <h4 className="text-primary mb-3">📅 {dayName}</h4>
                  
                  {Object.entries(dayData.slots).map(([slotName, slotData]) => 
                    renderDaySlotSummary(dayName, slotName, slotData)
                  )}
                </div>
              );
            })}

            {Object.values(weeklyReport.reportByDay).every(
              day => Object.values(day.slots).every(slot => slot.customers.length === 0)
            ) && (
              <div className="text-center text-muted py-5">
                <h5>No standing orders found for any day</h5>
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

export default WeeklyReportTab;
